import React, { useState, useEffect } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import {
  IonContent,
  IonPage,
  IonButton,
  useIonToast,
  IonItem,
  IonInput,
  IonIcon,
  IonList,
  IonLabel,
} from "@ionic/react";
import Header from "src/components/Header";
import PanelFiscalizacion from "src/components/Fiscalizacion/PanelFiscalizacion";
import "./Fiscalizacion.css";
import {
  fetchCatalogoProductos,
  fetchEliminarProducto,
  fetchProductos,
  fetchProductosPost,
  ICatalogoProducto,
  IProductos,
} from "src/services/Fiscalizacion/Fiscalizacion";
import { TablaGenerica } from "src/components/DataTableMaterial";
import { search } from "ionicons/icons";
import { useFiscalizacion } from "src/components/Fiscalizacion/FiscalizacionContext";

const PRODUCTOS_POR_PAGINA = 5;
// Porcentaje con el que entra todo producto: el fiscal solo lo baja si corresponde
const PORCENTAJE_POR_DEFECTO = 100;
// Mínimo de caracteres para buscar en el catálogo de BC (son miles de ítems)
const MIN_CARACTERES_CATALOGO = 2;
// Tope de sugerencias mostradas en el buscador
const MAX_SUGERENCIAS = 25;

const FiscalizarStock: React.FC<RouteComponentProps> = ({ history }) => {
  const {setInformacionFiscalizacionStock} = useFiscalizacion()
  const { pathname } = history.location;
  const params = pathname.split("/");

  // El fiscalizacion_id se encuentra en la posición 3 del array params
  const fiscalizacion_id = params[5];
  const [present] = useIonToast();
  const [productos, setProductos] = useState<IProductos[]>([]);
  const [catalogo, setCatalogo] = useState<ICatalogoProducto[]>([]);
  const [productosSeleccionados, setProductosSeleccionados] = useState<IProductos[]>([]);
  // Se marca lo modificado por CodItem y no por indice: al eliminar una fila los
  // indices se corren y se terminaba guardando el producto equivocado.
  const [codigosModificados, setCodigosModificados] = useState<string[]>([]);
  const [inputFocused, setInputFocused] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState<string>("");
  const [paginaActual, setPaginaActual] = useState(1);

  useEffect(() => {
    const cargarProductos = async () => {
      try {
        // El catálogo de BC se pide en paralelo: permite fiscalizar un ítem que
        // todavía no tiene historial en el PS
        const [resultado, itemsCatalogo] = await Promise.all([
          fetchProductos(parseInt(fiscalizacion_id)),
          fetchCatalogoProductos(),
        ]);

        setCatalogo(itemsCatalogo);

        if (!resultado.error) {
          // Mapeamos los productos: la cantidad solo viene cargada si ya se fiscalizó
          // (origen APP); el porcentaje arranca en 100% para todos
          const productosProcesados = resultado.datos.flat().map((producto) => ({
            ...producto,
            CantidadStock: producto.origen === "APP" ? producto.CantidadStock || 0 : 0,
            porcentaje:
              producto.origen === "APP" && producto.porcentaje
                ? producto.porcentaje
                : PORCENTAJE_POR_DEFECTO,
          }));
          setProductos(productosProcesados); // Actualizamos el estado de productos
          setProductosSeleccionados(productosProcesados); // Inicializamos los seleccionados
        } else {
          console.error(resultado.error);
        }
      } catch (error) {
        console.error("Error al cargar productos:", error);
      }
    };

    // Verificar si hay un fiscalizacion_id válido antes de cargar los productos
    if (fiscalizacion_id) {
      cargarProductos();
    }
  }, [fiscalizacion_id]);

  const codigoProducto = (producto: { CodItem?: string }) =>
    String(producto?.CodItem || "").trim().toUpperCase();

  const handleProductoSeleccionado = (producto: IProductos) => {
    // Un mismo CodItem no puede quedar dos veces en la fiscalización
    const yaEsta = productosSeleccionados.some(
      (item) => codigoProducto(item) === codigoProducto(producto)
    );

    if (yaEsta) {
      present({
        color: "warning",
        message: "El producto ya está en la lista",
        duration: 3000,
      });
      setSelectedProducto("");
      setInputFocused(false);
      return;
    }

    const nuevosProductosSeleccionados = [
      ...productosSeleccionados,
      { ...producto, porcentaje: producto.porcentaje || PORCENTAJE_POR_DEFECTO },
    ];
    setProductosSeleccionados(nuevosProductosSeleccionados);
    // Se marca como modificado para que "Guardar" lo persista aunque el fiscal
    // no toque la cantidad (queda fiscalizado en 0)
    marcarModificado(producto);
    setPaginaActual(
      Math.max(1, Math.ceil(nuevosProductosSeleccionados.length / PRODUCTOS_POR_PAGINA))
    );
    setSelectedProducto(""); // Limpiar el campo de búsqueda después de seleccionar
    setInputFocused(false); // Cerrar la lista de productos filtrados
  };

  const marcarModificado = (producto: IProductos) => {
    const codigo = codigoProducto(producto);

    setCodigosModificados((prev) =>
      prev.includes(codigo) ? prev : [...prev, codigo]
    );
  };

  const handleCantidadChange = (cantidad: number, index: number) => {
    const nuevosProductosSeleccionados = [...productosSeleccionados];
    nuevosProductosSeleccionados[index] = {
      ...nuevosProductosSeleccionados[index],
      CantidadStock: cantidad,
    };
    setProductosSeleccionados(nuevosProductosSeleccionados);
    marcarModificado(nuevosProductosSeleccionados[index]);
  };

  const handlePorcentajeChange = (porcentaje: number, index: number) => {
    const nuevosProductosSeleccionados = [...productosSeleccionados];
    nuevosProductosSeleccionados[index] = {
      ...nuevosProductosSeleccionados[index],
      porcentaje: porcentaje,
    };
    setProductosSeleccionados(nuevosProductosSeleccionados);
    marcarModificado(nuevosProductosSeleccionados[index]);
  };

  const handleEliminarProducto = async (producto: IProductos) => {
    const codigo = codigoProducto(producto);
    const etiqueta = producto.DescripcionItem || producto.CodItem;

    if (!window.confirm(`¿Quitar "${etiqueta}" de la fiscalización?`)) return;

    // Se pide el borrado siempre, sin mirar el origen: un producto agregado del
    // catálogo y ya guardado sigue figurando como CATALOGO en memoria (la lista no
    // se recarga al guardar) y su fila quedaría huérfana. Si no hay fila que borrar,
    // el DELETE no afecta a nadie.
    const respuesta = await fetchEliminarProducto(
      parseInt(fiscalizacion_id),
      producto.CodItem
    );

    if (!respuesta.exito) {
      present({
        color: "danger",
        message: respuesta.info || "No se pudo eliminar el producto",
        duration: 4000,
      });
      return;
    }

    setProductosSeleccionados((prev) =>
      prev.filter((item) => codigoProducto(item) !== codigo)
    );
    // Vuelve a estar disponible en el buscador, en blanco: si lo agregan de nuevo
    // no tiene que reaparecer con la cantidad que se acaba de borrar.
    setProductos((prev) =>
      prev.map((item) =>
        codigoProducto(item) === codigo
          ? { ...item, origen: "PP", CantidadStock: 0, porcentaje: PORCENTAJE_POR_DEFECTO }
          : item
      )
    );
    setCodigosModificados((prev) => prev.filter((cod) => cod !== codigo));

    present({
      color: "success",
      message: "Producto quitado de la fiscalización",
      duration: 3000,
    });
  };

  const handleGuardarProductos = async () => {
    try {
      const productosModificados = productosSeleccionados.filter((producto) =>
        codigosModificados.includes(codigoProducto(producto))
      );

      setInformacionFiscalizacionStock(productosModificados);

      const response = await fetchProductosPost(
        parseInt(fiscalizacion_id),
        productosModificados
      );
      if (response.exito) {
        present({
          color: "success",
          message: response.info,
          duration: 3000,
        });
        setCodigosModificados([]);
      } else {
        present({
          color: "danger",
          message:
            "Ocurrió un error al guardar los datos verifica la fecha y el estado nuevamente",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error al guardar productos:", error);
    }
  };

  // Sugerencias del buscador: primero el histórico del PS (últimos pedidos + lo ya
  // fiscalizado) y después el catálogo de BC, sin repetir códigos ni ofrecer lo que
  // ya está en la tabla.
  // Igual que en el pedido: un punto adelante restringe la búsqueda al código.
  // Sin punto se busca en la descripción y también en el código.
  const entrada = selectedProducto.trim().toLowerCase();
  const buscaPorCodigo = entrada.startsWith(".");
  const termino = (buscaPorCodigo ? entrada.slice(1) : entrada).trim();

  const coincide = (codigo: string, descripcion: string) =>
    !termino ||
    (buscaPorCodigo
      ? codigo.toLowerCase().includes(termino)
      : descripcion.toLowerCase().includes(termino) ||
        codigo.toLowerCase().includes(termino));

  const codigosEnTabla = new Set(productosSeleccionados.map(codigoProducto));

  const sugerenciasHistorico = productos.filter(
    (producto) =>
      !codigosEnTabla.has(codigoProducto(producto)) &&
      coincide(producto.CodItem || "", producto.DescripcionItem || "")
  );

  const codigosOfrecidos = new Set(
    productosSeleccionados
      .map(codigoProducto)
      .concat(sugerenciasHistorico.map(codigoProducto))
  );

  const sugerenciasCatalogo: IProductos[] =
    termino.length < MIN_CARACTERES_CATALOGO
      ? []
      : catalogo
          .filter(
            (item) =>
              !codigosOfrecidos.has(String(item.prod_nombre || "").trim().toUpperCase()) &&
              coincide(item.prod_nombre || "", item.prod_descripcion || "")
          )
          .map((item) => ({
            porcentaje: PORCENTAJE_POR_DEFECTO,
            cantidad: 0,
            CantidadStock: 0,
            CantidadSugerida: 0,
            CodItem: String(item.prod_nombre || "").trim(),
            Cont: 0,
            DescripcionItem: item.prod_descripcion || "",
            PrecioTotal: "",
            PrecioUnitario: String(item.precio_unitario ?? ""),
            fecha: "",
            origen: "CATALOGO",
          }));

  const filteredProductos = [...sugerenciasHistorico, ...sugerenciasCatalogo].slice(
    0,
    MAX_SUGERENCIAS
  );

  const totalPaginas = Math.max(
    1,
    Math.ceil(productosSeleccionados.length / PRODUCTOS_POR_PAGINA)
  );

  const indiceInicial = (paginaActual - 1) * PRODUCTOS_POR_PAGINA;
  const productosPaginados: Array<IProductos & { indiceOriginal: number }> = productosSeleccionados
    .slice(indiceInicial, indiceInicial + PRODUCTOS_POR_PAGINA)
    .map((producto, index) => ({
      ...producto,
      indiceOriginal: indiceInicial + index,
    }));

  useEffect(() => {
    if (paginaActual > totalPaginas) {
      setPaginaActual(totalPaginas);
    }
  }, [paginaActual, totalPaginas]);

  return (
    <IonPage>
      <Header titulo="Fiscalización - Stock" />
      <IonContent id="fiscalizacion">
        <div className="fisContainer">
          <PanelFiscalizacion />
          <div className="fisCard">
            <div className="fisCard_title--area">
              <img src="assets/icon/fisc-COD6.svg" alt="Stock" />
              <h1>Stock de productos</h1>
            </div>

            <IonItem>
              <IonInput
                value={selectedProducto}
                onIonChange={(e: any) => setSelectedProducto(e.detail.value)}
                onFocus={() => setInputFocused(true)}
                clearInput={true}
                placeholder="Buscar producto por nombre o código"
              ></IonInput>
              <IonIcon icon={search} />
            </IonItem>
            {inputFocused && (
              <IonList>
                {filteredProductos.map((producto, index) => (
                  <IonItem
                    key={`${producto.CodItem}-${index}`}
                    button
                    onClick={() => handleProductoSeleccionado(producto)}
                  >
                    <IonLabel>
                      <h3>{producto.DescripcionItem}</h3>
                      <p>
                        {producto.CodItem}
                        {producto.origen === "CATALOGO" ? " · catálogo" : ""}
                      </p>
                    </IonLabel>
                  </IonItem>
                ))}
                {filteredProductos.length === 0 && (
                  <IonItem>
                    <IonLabel color="medium">
                      {selectedProducto.trim().length < MIN_CARACTERES_CATALOGO
                        ? "Escribí al menos 2 letras para buscar en el catálogo"
                        : "Sin resultados"}
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            )}

            <TablaGenerica
              data={productosPaginados as IProductos[]}
              handleCantidadChange={(cantidad, index) =>
                handleCantidadChange(cantidad, productosPaginados[index].indiceOriginal)
              }
              handlePorcentajeChange={(porcentaje, index) =>
                handlePorcentajeChange(
                  porcentaje,
                  productosPaginados[index].indiceOriginal
                )
              }
              handleEliminar={(index) =>
                handleEliminarProducto(productosPaginados[index])
              }
            />

            {productosSeleccionados.length > 0 && (
              <div className="fisPagination">
                <button
                  type="button"
                  className="fisPagination_btn"
                  disabled={paginaActual === 1}
                  onClick={() => setPaginaActual((prev) => Math.max(1, prev - 1))}
                >
                  Anterior
                </button>
                <span className="fisPagination_text">
                  Página {paginaActual} de {totalPaginas}
                </span>
                <button
                  type="button"
                  className="fisPagination_btn"
                  disabled={paginaActual === totalPaginas}
                  onClick={() =>
                    setPaginaActual((prev) => Math.min(totalPaginas, prev + 1))
                  }
                >
                  Siguiente
                </button>
              </div>
            )}

            <IonButton onClick={handleGuardarProductos}>Guardar</IonButton>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default withRouter(FiscalizarStock);
